// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component,
  EventEmitter,
  Input, OnDestroy, OnInit, Output,
  ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import {
  MatDialog,
  MatOptionSelectionChange, MatPaginator, MatSort
} from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  startWith, tap
} from "rxjs/operators";

// Core packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { PlatformNote } from "src/app/shared/models/platform-note/platform-note.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { PlatformNotesDataSource } from "src/app/shared/utils/data-sources/platform-notes-data-source";
import { AddPlatformNoteDialogComponent } from "./components/add-platform-note-dialog/add-platform-note-dialog.component";
import { AssociateBalancesPlatformNoteDialogComponent } from "./components/associate-balances-platform-note-dialog/associate-balances-platform-note-dialog.component";
import { AssociatePropertiesPlatformNoteDialogComponent } from "./components/associate-properties-platform-note-dialog/associate-properties-platform-note-dialog.component";
import { EditPlatformNoteDialogComponent } from "./components/edit-platform-note-dialog/edit-platform-note-dialog.component";
import { ViewPlatformNoteDialogComponent } from "./components/view-platform-note-dialog/view-platform-note-dialog.component";
import { PlatformNotesService } from "./platformNotes.service";

@Component({
  selector: "app-platform-note-card",
  templateUrl: "./platform-note-card.component.html",
  styleUrls: ["./platform-note-card.component.scss"]
})
export class PlatformNoteCardComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  @Input('companyId') companyId: string;
  @Input('propertyId') propertyId: string;
  @Input('historical') historical: boolean;
  @Input('reload') reload: boolean;
  @Input('expanded') expanded = false;
  @Output('eventDeactivate') eventDeactivate = new EventEmitter<boolean>();
  @Output('eventDelete') eventDelete = new EventEmitter<boolean>();

  lang = this.translateService.currentLang || this.translateService.defaultLang;
  
  loggedUser: IUser;
  loggedUserPermissions: string[];
  currentRole: any;
  loggedUserMaster = false;
  
  // BEGIN - Table properties
  displayedColumns: string[] = [
    "index",
    "category",
    "title",
    "createdAt",
    "createdBy",
    "file",
    "actions"
  ];
  dataSource: PlatformNotesDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  categoryFilter: FormControl = new FormControl("");
  titleFilter: FormControl = new FormControl("");
  createdAtFilter: FormControl = new FormControl("");
  createdByFilter: FormControl = new FormControl("");
  // END - Table properties

  availableCreatedBy: User[] = [];
  creatorsIsLoading = false;

  title = '';

  deactivateEnable = false;
  isPoperty = false;

  /**
   * Class constructor
   */
  constructor(
    private cookieService: CookieService,
    private platformNotesService: PlatformNotesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    public dialog: MatDialog,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // card title
    if (this.historical) {
      this.title = this.translateService.instant(
        "platformNotes.historicalTitle"
      )
    } else {
      this.title = this.translateService.instant(
        "platformNotes.title"
      )
    }

    // From property
    if (this.propertyId && this.propertyId !== '') {
      this.isPoperty = true;
    }

    // Deactivate enable
    if (
      !this.historical && 
      !this.isPoperty
    ) {
      this.deactivateEnable = true;
    }
    
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        if (!this.loggedUser) return;
        this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        
        if (
          this.currentRole &&
          this.currentRole.role.type === "master" ||
          this.currentRole.role.type === "collaborator"
        ) {
          this.loggedUserMaster = true;
        }
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init dataSources
    this.dataSource = new PlatformNotesDataSource(
      this.platformNotesService,
      this.helperService
    );

    // Get creators
    this.getCreators();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadPage()))
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.categoryFilter.valueChanges.pipe(startWith(null as string)),
        this.titleFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
        this.createdByFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe(res => {
          // Handle ceator filter
          if (res[3]) {
            this.creatorsIsLoading = true;
            this.getCreators({
              general: this.createdByFilter.value._id ? this.createdByFilter.value._id : this.createdByFilter.value,
            });
          }

          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.loadPage();
          }
        })
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      "platformNotes",
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        category: this.categoryFilter.value,
        title: this.titleFilter.value,
        createdAt: this.createdAtFilter.value,
        createdBy: this.createdByFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      category: this.categoryFilter.value,
      title: this.titleFilter.value,
      createdAt: this.createdAtFilter.value
        ? new Date(this.createdAtFilter.value).toISOString()
        : undefined,
      createdBy: this.createdByFilter.value ? this.createdByFilter.value._id : undefined,
    };
    this.dataSource.loadItems(
      this.historical,
      this.companyId,
      this.propertyId ? this.propertyId : '',
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Export companies in a file with given format
   *
   * @since 1.0.0
   *
   * @param format The format. Can be 'excel' or 'csv'
   */
  onExport(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    if (!this.isPoperty) {
      const fileName = `${this.translateService.instant(
        "platformNotes.exportCompanyFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.platformNotesService.getCompanyExport(this.companyId, this.historical).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    } else {
      const fileName = `${this.translateService.instant(
        "platformNotes.exportPropertyFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.platformNotesService.getPropertyExport(this.propertyId, this.historical).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.categoryFilter.setValue(null);
    this.titleFilter.setValue(null);
    this.createdAtFilter.setValue(null);
    this.createdByFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "createdAt";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.category) {
                this.categoryFilter.setValue(setting.search.category);
              }
              if (setting.search && setting.search.title) {
                this.titleFilter.setValue(setting.search.title);
              }
              if (setting.search && setting.search.createdAt) {
                this.createdAtFilter.setValue(setting.search.createdAt);
              }
              if (setting.search && setting.search.createdBy) {
                this.createdByFilter.setValue(setting.search.createdBy);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Add new note
   */
  onAdd(): void {
    // Open dialog
    const dialogRef = this.dialog.open(AddPlatformNoteDialogComponent, {
      minWidth: "60%",
      maxHeight: "98vh",
      data: {
        companyId: this.companyId,
        propertyId: this.propertyId,
      },
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((res) => {
        if (typeof res !== "undefined" && res.status) {
          this.loadPage();
          return;
        }
      })
    );
  }
  
  /**
   * View note
   */
  onView(element: PlatformNote): void {
    console.log(element)
    // Open dialog
    const dialogRef = this.dialog.open(ViewPlatformNoteDialogComponent, {
      minWidth: "40%",
      maxHeight: "98vh",
      data: {
        item: element,
        companyId: this.companyId,
      },
    });
  }
  
  /**
   * Edit note
   */
  onEdit(element: PlatformNote): void {
    console.log(element)
    // Open dialog
    const dialogRef = this.dialog.open(EditPlatformNoteDialogComponent, {
      minWidth: "60%",
      maxHeight: "98vh",
      data: {
        item: element,
        companyId: this.companyId,
      },
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((res) => {
        if (typeof res !== "undefined" && res.status) {
          this.loadPage();
          return;
        }
      })
    );
  }
  
  /**
   * Deactivate note
   */
  onDeactivate(element: PlatformNote): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('platformNotes.dialog.deactivate.title'),
        message: this.translateService.instant('platformNotes.dialog.deactivate.text'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.subscriptions.push(
          this.platformNotesService
            .deactivateNote(element._id).subscribe(
              res => {
                this.loadPage();
                this.eventDeactivate.emit(true);
              },
              err => {
                this.helperService.handleError(err);
              }
            )
        )
      }
    });
  }
  
  /**
   * Delete note
   */
  onDelete(element: PlatformNote): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('platformNotes.dialog.delete.title'),
        message: this.translateService.instant('platformNotes.dialog.delete.text'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.subscriptions.push(
          this.platformNotesService
            .deleteNote(element._id).subscribe(
              res => {
                this.loadPage();
              },
              err => {
                this.helperService.handleError(err);
              }
            )
        )
      }
    });
  }
  
  /**
   * Associate note
   */
  onAssociate(element: PlatformNote): void {
    if (this.isPoperty) {
      AssociateBalancesPlatformNoteDialogComponent
      // Open dialog
      const dialogRef = this.dialog.open(AssociateBalancesPlatformNoteDialogComponent, {
        minWidth: "40%",
        maxHeight: "98vh",
        data: {
          noteId: element._id,
          companyId: this.companyId,
          propertyId: this.propertyId,
          propertyBalances: element.propertyBalances,
          balances: element.balances,
        },
      });

      // On dialog closing, check if something is changed
      // If so, let's reload table data
      this.subscriptions.push(
        dialogRef.afterClosed().subscribe((res) => {
          if (typeof res !== "undefined" && res.status) {
            this.loadPage();
            return;
          }
        })
      );
    } else {
      // Open dialog
      const dialogRef = this.dialog.open(AssociatePropertiesPlatformNoteDialogComponent, {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          noteId: element._id,
          companyId: this.companyId,
          properties: element.properties,
          allProperties: element.allProperties
        },
      });

      // On dialog closing, check if something is changed
      // If so, let's reload table data
      this.subscriptions.push(
        dialogRef.afterClosed().subscribe((res) => {
          if (typeof res !== "undefined" && res.status) {
            this.loadPage();
            return;
          }
        })
      );
    }
  }

  /**
   * Can Edit and Delete note
   */
  getEditEnabled(element: PlatformNote): boolean {
    if (this.historical) {
      return false;
    }
    if (
      this.isPoperty &&
      element.createdIn === 'company'
    ) {
      return false;
    }
    return true;
  }

  /**
   * Can Edit/Delete only in company side
   */
  getEditInCompany(element: PlatformNote): boolean {
    if (this.historical) {
      return false;
    }
    if (
      this.isPoperty &&
      element.createdIn === 'company'
    ) {
      return true;
    }
    return false;
  }

  /**
   * Can Associate  note
   */
  getAssociateEnabled(element: PlatformNote): boolean {
    if (this.historical) {
      return false;
    }
    if (
      this.isPoperty &&
      element.category !== 'subscription' &&
      element.category !== 'work-account'
    ) {
      return false;
    }
    return true;
  }

  /**
   * Get creatos
   */
  getCreators(search?: any): void {
    this.creatorsIsLoading = true;

    this.subscriptions.push(
      this.platformNotesService
        .getCreators(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.creatorsIsLoading = false;
          })
        )
        .subscribe((res) => {
          if (res.status) {
            this.availableCreatedBy = res.data.items;
          }
        })
    )
  }
  
  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayCreatorFn(arg?: IUser): string | undefined {
    return arg ? `${arg.firstName} ${arg.lastName}` : undefined;
  }
  
  /**
   * Handle click on a selected admin in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onCreatorFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.createdByFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }
}